import React, { useEffect, useState } from 'react';
import { normalizeSitecoreImageUrl } from '../ImageWrapper/ImageWrapper';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { parseUrlObject } from 'lib/utils/string-utils';
export interface SvgImageWrapperProps {
  src: string; // URL or path to the SVG
  fallback?: React.ReactNode; // Shown while loading or on error
  alt?: string; // Accessible label
  className?: string; // CSS classes
  sanitize?: boolean; // Enable basic built-in sanitization
  shouldHaveAriaLabel?: boolean; // Whether to add aria-label to the svg
}

export const SvgImageWrapper: React.FC<SvgImageWrapperProps> = ({
  src,
  fallback = null,
  alt = '',
  className = '',
  sanitize = true,
  shouldHaveAriaLabel = true,
}) => {
  const [error, setError] = useState<boolean>(false);

  const { sitecoreContext } = useSitecoreContext();

  const svgCache = sitecoreContext.svgCache;

  const normalizedSrc = normalizeSitecoreImageUrl(src);
  const cachedSvg = svgCache?.[getSvgCacheKey(normalizedSrc)];

  const sanitizedSvg = cachedSvg ? sanitizeSvg(cachedSvg, className) : null;

  const [svgHtml, setSvgHtml] = useState<string | null>(sanitizedSvg);

  useEffect(() => {
    const setSvg = async () => {
      if (cachedSvg) {
        setError(false);
        setSvgHtml(sanitizedSvg);
        return;
      }
      if (!isFetchableSvgSource(normalizedSrc)) {
        setError(true);
        setSvgHtml(null);
        return;
      }

      let svgText = await fetchSvg();

      if (svgText) {
        if (sanitize) {
          svgText = sanitizeSvg(svgText, className);
        }
        setError(false);
        setSvgHtml(svgText);
      } else {
        setError(true);
        setSvgHtml(null);
      }
    };
    const fetchSvg = async () => {
      try {
        const svgSrc = normalizedSrc;

        if (!isFetchableSvgSource(svgSrc)) {
          setError(true);
          setSvgHtml(null);
          return;
        }

        if (svgSrc.startsWith('data:image/svg+xml')) {
          return decodeSvgDataUri(svgSrc);
        }

        const response = await fetch(svgSrc);
        if (!response.ok) {
          return null;
        }

        const contentType = response.headers.get('content-type')?.toLowerCase() || '';
        if (!contentType.includes('image/svg')) {
          return null;
        }

        const svgText = await response.text();

        return svgText;
      } catch (_err) {
        return null;
      }
    };
    setSvg();
  }, [sanitize, className, svgCache, normalizedSrc, cachedSvg, sanitizedSvg]);

  if (error || !svgHtml) {
    return <div className={className}>{fallback}</div>;
  }

  return (
    <div
      role={shouldHaveAriaLabel ? 'img' : undefined}
      aria-label={shouldHaveAriaLabel ? alt : undefined}
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
  );
};

export function getSvgCacheKey(normalizedSrc: string | undefined) {
  return normalizedSrc?.split('?')[0] ?? '';
}

function isFetchableSvgSource(src: string | undefined): src is string {
  if (!src) {
    return false;
  }

  const normalizedSrc = src.trim();

  if (normalizedSrc.startsWith('data:image/svg+xml')) {
    return true;
  }

  const parsedUrl = parseUrlObject(normalizedSrc);
  const pathname = parsedUrl?.pathname?.toLowerCase() || '';

  return pathname.endsWith('.svg');
}

function decodeSvgDataUri(src: string): string | null {
  const [, payload = ''] = src.split(',', 2);
  if (!payload) {
    return null;
  }

  try {
    if (/;base64,/i.test(src)) {
      return atob(payload);
    }

    return decodeURIComponent(payload);
  } catch {
    return null;
  }
}

function sanitizeSvg(svgText: string, className: string = '') {
  return svgText
    .replace(/<script.*?>.*?<\/script>/gi, '')
    .replace(/on\w+=".*?"/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/<svg([^>]+)?/i, `<svg$1 class="${className}"`)
    .replace(/<path([^>]*?)\/?>/gi, (_match, attrs) => {
      return `<path${attrs} class="${className}" />`;
    });
}
